import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { getProducts, getCategories } from '../../../lib/api';
import { Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useManagerPortalConfig } from '../manager/useManagerPortalConfig';

const COURSE_LABELS = ['Vorspeise', 'Hauptgang', 'Nachspeise', 'Getränk', 'Dessert'];

export default function AdminArticleCourses() {
  const { portalConfig, savePortalConfig } = useManagerPortalConfig();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const courseAssignments = portalConfig?.articleCourses || {};

  const filteredProducts = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    return products.filter((product) => {
      if (selectedCategory !== 'all' && product.category_id !== selectedCategory) return false;
      if (!normalized) return true;
      return product.name.toLowerCase().includes(normalized) || (product.description || '').toLowerCase().includes(normalized);
    });
  }, [products, search, selectedCategory]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [prods, cats] = await Promise.all([getProducts(), getCategories()]);
      setProducts(prods);
      setCategories(cats);
    } catch (error) {
      toast.error('Fehler beim Laden der Ganggruppen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCourseChange = async (productId, nextCourse) => {
    setSavingId(productId);
    try {
      await savePortalConfig(
        (prev) => ({
          ...prev,
          articleCourses: {
            ...(prev?.articleCourses || {}),
            [productId]: nextCourse,
          },
        }),
        'Ganggruppe gespeichert',
      );
      setSavingId(null);
      toast.success('Ganggruppe gespeichert');
    } catch (error) {
      setSavingId(null);
      toast.error('Fehler beim Speichern der Ganggruppe');
    }
  };

  const categoryMap = useMemo(() => Object.fromEntries(categories.map((cat) => [cat.id, cat.name])), [categories]);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-400">Artikel</p>
        <h1 className="text-3xl font-bold text-[#132d39]">Ganggruppen</h1>
        <p className="max-w-3xl text-slate-600">
          Pflegen Sie Ganggruppen für Ihre Küche und POS-Anzeige. Artikel können einer Ganggruppe zugeordnet werden, um Abläufe in der Küche zu strukturieren.
        </p>
      </div>

      <Card className="rounded-[28px] border-slate-200 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl font-bold tracking-tight text-[#314b59]">Gangzuordnung</CardTitle>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
              <Search className="w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Artikel suchen..."
                className="border-none bg-transparent p-0 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-2" />Aktualisieren
              </Button>
              <select
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
              >
                <option value="all">Alle Kategorien</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-slate-500 uppercase tracking-[0.12em]">
                  <th className="px-4 py-3">Artikel</th>
                  <th className="px-4 py-3">Kategorie</th>
                  <th className="px-4 py-3">Ganggruppe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {isLoading ? (
                  <tr><td colSpan="3" className="px-4 py-12 text-center text-slate-400">Lade Artikel…</td></tr>
                ) : filteredProducts.length === 0 ? (
                  <tr><td colSpan="3" className="px-4 py-12 text-center text-slate-400">Keine Artikel gefunden.</td></tr>
                ) : filteredProducts.map((product) => (
                  <tr key={product.id} className="bg-white hover:bg-slate-50">
                    <td className="px-4 py-4 font-medium text-slate-900">{product.name}</td>
                    <td className="px-4 py-4 text-slate-600">{categoryMap[product.category_id] || 'Unbekannt'}</td>
                    <td className="px-4 py-4">
                      <select
                        value={courseAssignments[product.id] || 'Hauptgang'}
                        onChange={(event) => handleCourseChange(product.id, event.target.value)}
                        disabled={savingId === product.id}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
                      >
                        {COURSE_LABELS.map((course) => (
                          <option key={course} value={course}>{course}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-slate-500">Die Ganggruppe wird dem Artikel direkt zugeordnet und im Management-Bereich gespeichert.</p>
        </CardContent>
      </Card>
    </div>
  );
}
